import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Settings, Save } from 'lucide-react';
import { LogoSpinner, CircularSpinner, DotsSpinner } from '../components/LoadingSpinner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SpinnerSettings = () => {
  const { getAuthHeader } = useAuth();
  const [settings, setSettings] = useState({
    public_site_spinner: 'logo',
    admin_site_spinner: 'circular'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/loading-spinner/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching spinner settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await axios.put(`${API}/admin/loading-spinner/settings`, settings, {
        headers: getAuthHeader()
      });
      alert('Spinner settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center min-h-screen"><CircularSpinner /></div>;
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2" style={{ color: '#A08EA8' }}>
            <Settings className="w-8 h-8" />
            Loading Spinner Settings
          </h1>
          <p className="text-gray-600 mt-1">Configure loading spinners for public and admin sites</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-4 py-2 rounded-lg text-white font-medium flex items-center gap-2"
          style={{ background: 'linear-gradient(135deg, #A08EA8 0%, #7D6882 100%)' }}
        >
          <Save className="w-4 h-4" />
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Public Site Spinner */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4" style={{ color: '#A08EA8' }}>Public Website</h2>
          <p className="text-gray-600 text-sm mb-4">Choose the loading spinner for your public-facing website</p>
          
          <div className="space-y-4">
            {['logo', 'circular', 'dots'].map(type => (
              <div 
                key={type}
                className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                  settings.public_site_spinner === type ? 'border-purple-500 bg-purple-50' : 'border-gray-200'
                }`}
                onClick={() => setSettings({ ...settings, public_site_spinner: type })}
              >
                <div className="flex items-center justify-between mb-2">
                  <label className="font-medium flex items-center gap-2">
                    <input
                      type="radio"
                      name="public_spinner"
                      checked={settings.public_site_spinner === type}
                      onChange={() => {}}
                      style={{ accentColor: '#A08EA8' }}
                    />
                    {type === 'logo' ? 'Animated Logo' : type === 'circular' ? 'Circular Spinner' : 'Pulsing Dots'}
                  </label>
                </div>
                <div className="flex justify-center bg-white rounded p-4">
                  {type === 'logo' ? <LogoSpinner /> : type === 'circular' ? <CircularSpinner /> : <DotsSpinner />}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Admin Site Spinner */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4" style={{ color: '#A08EA8' }}>Admin Panel</h2>
          <p className="text-gray-600 text-sm mb-4">Choose the loading spinner for your admin panel</p>
          
          <div className="space-y-4">
            {['logo', 'circular', 'dots'].map(type => (
              <div 
                key={type}
                className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                  settings.admin_site_spinner === type ? 'border-purple-500 bg-purple-50' : 'border-gray-200'
                }`}
                onClick={() => setSettings({ ...settings, admin_site_spinner: type })}
              >
                <div className="flex items-center justify-between mb-2">
                  <label className="font-medium flex items-center gap-2">
                    <input
                      type="radio"
                      name="admin_spinner"
                      checked={settings.admin_site_spinner === type}
                      onChange={() => {}}
                      style={{ accentColor: '#A08EA8' }}
                    />
                    {type === 'logo' ? 'Animated Logo' : type === 'circular' ? 'Circular Spinner' : 'Pulsing Dots'}
                  </label>
                </div>
                <div className="flex justify-center bg-white rounded p-4">
                  {type === 'logo' ? <LogoSpinner /> : type === 'circular' ? <CircularSpinner /> : <DotsSpinner />}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpinnerSettings;
